import _ from 'lodash'
import googlePhone from 'google-libphonenumber'
import * as CommunicationTypes from 'src/constants/communication-types'
import { CONTACTS_ACCESS_EVERYONE } from 'src/constants/contact-access-types'
import teamInboxPropsMixin from 'src/plugins/mixins/teaminbox.props.mixin'
import { DEFAULT_PINNED_LIST } from 'src/constants/contacts-list-default-pinned-list'
import { CALENDAR_V2_ACTIVITY_LOG_SOURCE, CALENDAR_V2_BOOKING_SOURCE, CalendarV2BookingEvents } from 'src/constants/calendar-connections'
import { CONTACT_PHONE_NUMBERS_CHANGED_EVENT } from 'src/constants/hubspot-jit-card'
import talk2Api from 'src/plugins/api/api'
import talk2TeamInboxApi from 'src/plugins/api/teamInboxApi'
import * as storage from 'src/plugins/helpers/storage'
import { mapActions, mapState } from 'vuex'
import { TEAMINBOXES_MENU_TITLE } from 'src/router/routes'
import { ALL_INBOXES_ID } from 'src/store/teaminbox/teaminbox.store'
import { WIDGET_PLATFORMS } from 'src/utils/widget-platform-detector'

const CONTACT_REDIRECT_TOAST_SESSION_KEY = 'talk2_contact_redirect_toast_to_contact_id'

const CONTACT_DELETED_WIDGET_ERROR_TITLE = 'Contact Not In Aloware'
const CONTACT_DELETED_WIDGET_ERROR_MESSAGE = 'This contact was deleted in Aloware. Reload this record to start a new conversation.'

export default {
  mixins: [teamInboxPropsMixin],

  data () {
    return {
      hasMoreCommunications: true,
      selectedPhoneNumber: null,
      selectedCampaignId: null,
      communicationsAndAudits: [],
      loadingContact: false,
      loadingContactCommunications: false,
      loadingSendMessage: false,
      loadingMarkAsRead: false,
      isScrolling: false,
      selectedContact: {
        id: null,
        first_name: null,
        last_name: null,
        cnam_country: null,
        cnam_state: null,
        cnam_city: null,
        cnam_zipcode: null,
        company_name: null,
        address: null,
        email: null,
        timezone: null,
        notes: '',
        date_of_birth: false,
        phone_number: null,
        is_blocked: false,
        communications: [],
        campaign_ids: [],
        user: null,
        user_id: null,
        lrn_type: null,
        is_dnc: false,
        tags: [],
        tag_ids: [],
        unread_count: 0,
        unread_voicemails_count: 0,
        unread_missed_calls_count: 0,
        unread_texts_count: 0
      },
      selectedContactCampaigns: [],
      communicationsPage: 1,
      communicationsPerPage: 10,
      canEmail: false,
      type: 0,
      smsOnly: false,
      isLoadingPreviousActivities: false,
      activityTypes: [
        'communication',
        'contact-audit'
      ],
      // push to CRM feature exclusive for ss, just in case new company is added, then just add the reseller id here
      resellerIdToPushContactToCrm: [357],
      messageObject: {
        phone_number: null
      },
      activeNames: ['phone_numbers', 'about', 'lines', 'ring-groups'],
      contactId: null,
      cancelToken: null,
      source: null,
      communicationApiCancelToken: null,
      communicationApiSource: null,
      contactActivitiesInterval: null,
      containerElInterval: null,
      scrollInterval: null,
      skipComponents: [
        'line-selector',
        'contact-details',
        'message-composer'
      ],
      listeners: {},
      phoneNumbersRequestId: 0,
      contactFetchFailed: false,
      lastLineUsedWatcher: null,
      currentContactFetchRequestId: 0
    }
  },

  computed: {
    ...mapState('auth', [
      'profile'
    ]),

    ...mapState(['campaigns', 'auth']),

    ...mapState('contacts', [
      'contact',
      'communicationsSummary',
      'newCommunicationInprogressContactFetch'
    ]),

    ...mapState('inbox', { selectContact: 'selectedContact' }),

    ...mapState('cache', ['currentCompany']),

    ...mapState('TeamInbox', ['contactsLastUsedLines', 'contactsLastUsedLinesUpdatedAt']),

    selectedCampaign () {
      if (this.campaigns) {
        return this.campaigns.find(campaign => campaign.id === this.selectedCampaignId)
      }

      return null
    },

    filteredCommunications () {
      let communications = []

      if (this.communicationsAndAudits) {
        if (this.type !== undefined && this.type === 0) {
          // returns all communications
          communications = this.communicationsAndAudits
        } else if (this.type !== undefined && this.type === CommunicationTypes.NOTE) {
          // returns all note communications
          communications = this.communicationsAndAudits.filter(communication =>
            CommunicationTypes.NOTE_TYPES.includes(communication?.type) || communication.type === undefined
          )
        } else if (this.type === undefined) {
          communications = this.communicationsAndAudits.filter(communication =>
            CommunicationTypes.NOTE_TYPES.includes(communication.type)
          )
        } else {
          // returns selected filter communications
          communications = this.communicationsAndAudits.filter(communication =>
            communication.type === this.type
          )
        }
      }

      return communications.reduce((acc, current) => {
        const x = acc.find(item => item.id === current.id)

        if (!x) {
          return acc.concat([current])
        }

        return acc
      }, [])
    },

    contactCampaignsFromCommunications () {
      if (this.contact && this.campaigns.length) {
        return this.campaignsAlphabeticalOrder
      }

      return []
    },

    otherCampaignsFromCommunications () {
      if (this.campaigns && this.contactCampaignsFromCommunications) {
        return _.difference(this.campaignsAlphabeticalOrder, this.contactCampaignsFromCommunications)
      }

      if (this.campaigns) {
        return this.campaignsAlphabeticalOrder
      }

      return []
    },

    campaignsAlphabeticalOrder () {
      if (this.campaigns) {
        return _.clone(this.campaigns).sort((a, b) => {
          const textA = a.name.toUpperCase()
          const textB = b.name.toUpperCase()

          return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
        })
      }

      return []
    },

    integrationsDisabled () {
      if (!this.currentCompany) {
        return true
      }

      if (this.currentCompany.id === 460) {
        return false
      }

      return !this.currentCompany.pipedrive_integration_enabled &&
        !this.currentCompany.hubspot_integration_enabled &&
        !this.currentCompany.stripe_integration_enabled &&
        !this.currentCompany.zoho_integration_enabled &&
        !this.currentCompany.guesty_integration_enabled
    },

    isPushContactToCrmEnabled () {
      return this.currentCompany
        ? this.resellerIdToPushContactToCrm.includes(this.currentCompany.reseller_id)
        : false
    },

    addTemporaryCommunication (communication) {
      this.communicationsAndAudits = [...this.communicationsAndAudits, communication]
    },

    isReadOnly () {
      if (!this.contact) {
        return false
      }

      return Boolean(this.contact.is_read_only) || false
    },

    isAllInboxesRoute () {
      return this.$route.params.inboxId === ALL_INBOXES_ID
    }
  },

  created () {
    this.contactId = _.get(this.$route, 'params.id', this.selectContact.id)
    this.cancelToken = window.axios.CancelToken
    this.source = this.cancelToken.source()

    this.communicationApiCancelToken = window.axios.CancelToken
    this.communicationApiSource = this.communicationApiCancelToken.source()

    if (this.skipComponents.includes(this.$options.name)) {
      return
    }

    this.listeners.newCommunication = (data) => {
      if (!this.checkCommunicationMatchesUserAccessibility(data, this.teamInbox)) {
        return
      }

      this.addNewCommunication(data)
      this.processContactUpdate(data.contact, data)
    }

    this.listeners.updateCommunication = (data) => {
      if (!this.checkCommunicationMatchesUserAccessibility(data, this.teamInbox)) {
        return
      }

      this.updateCommunication(data)
    }

    this.listeners.deleteCommunication = (data) => {
      this.deleteCommunication(data)
    }

    this.listeners.calendarV2BookingCreated = (booking) => {
      this.addNewCalendarV2Booking(booking)
    }

    this.listeners.calendarV2BookingUpdated = (booking) => {
      this.updateCalendarV2Booking(booking)
    }

    this.listeners.calendarV2BookingCancelled = (booking) => {
      this.updateCalendarV2Booking(booking)
    }

    this.listeners.contactUpdated = (data) => {
      // update the current contact
      this.processContactUpdate(data, null, true)
    }

    this.listeners.contactAuditCreated = (data) => {
      // check data loaded
      if (!_.isEmpty(this.contact) &&
        data.contact_id !== null &&
        data.contact_id !== undefined &&
        parseInt(data.contact_id) === parseInt(this.contact.id)) {
        this.updateSelectedContactAudit(data)
        this.scrollMessages()
      }
    }

    this.listeners.fetchContactInfo = (callback) => {
      this.processFetchContactInfo(callback)
    }

    this.listeners.contactPhoneNumbersChanged = (contactId) => {
      this.refreshContactPhoneNumbers(contactId)
    }

    this.listeners.updateContactInGroup = (contact) => {
      // update contact in the group of contacts
      // only in Contact page
      if (this.$route.name === 'Contact') {
        this.updateContacts(contact)
      }
    }

    this.listeners.markContactCommunicationsAllAsReadProcessed = (contact) => {
      this.markContactCommunicationsAllAsReadProcessed(contact)
    }
  },

  methods: {
    setRedirectedContactToastTarget (contactId) {
      try {
        window.sessionStorage.setItem(CONTACT_REDIRECT_TOAST_SESSION_KEY, String(contactId))
      } catch (e) {}
    },

    maybeShowRedirectedContactToast (contactId, contact) {
      let redirectContactId = null

      try {
        redirectContactId = window.sessionStorage.getItem(CONTACT_REDIRECT_TOAST_SESSION_KEY)
      } catch (e) {}

      if (!redirectContactId || String(contactId) !== String(redirectContactId)) {
        return
      }

      try {
        window.sessionStorage.removeItem(CONTACT_REDIRECT_TOAST_SESSION_KEY)
      } catch (e) {}

      const contactLabel = this.getContactDisplayName(contact) || `#${contactId}`
      this.$generalNotification(`Redirected to Contact ${contactLabel}`, 'info', 3000)
    },

    getContactDisplayName (contact) {
      const name = contact?.name
      if (name && String(name).trim()) {
        return String(name).trim()
      }

      const firstName = contact?.first_name
      const lastName = contact?.last_name
      const combined = [firstName, lastName].filter(Boolean).join(' ').trim()

      return combined || null
    },

    initListeners () {
      this.$VueEvent.listen('new_communication', this.listeners.newCommunication)
      this.$VueEvent.listen('update_communication', this.listeners.updateCommunication)
      this.$VueEvent.listen('delete_communication', this.listeners.deleteCommunication)
      this.$VueEvent.listen(CalendarV2BookingEvents.CREATED, this.listeners.calendarV2BookingCreated)
      this.$VueEvent.listen(CalendarV2BookingEvents.UPDATED, this.listeners.calendarV2BookingUpdated)
      this.$VueEvent.listen(CalendarV2BookingEvents.CANCELLED, this.listeners.calendarV2BookingCancelled)
      this.$VueEvent.listen('contact_updated', this.listeners.contactUpdated)
      this.$VueEvent.listen('contact_audit_created', this.listeners.contactAuditCreated)
      this.$VueEvent.listen('fetch_contact_info', this.listeners.fetchContactInfo)
      this.$VueEvent.listen(CONTACT_PHONE_NUMBERS_CHANGED_EVENT, this.listeners.contactPhoneNumbersChanged)
      this.$VueEvent.listen('update-contact-in-group', this.listeners.updateContactInGroup)
    },

    removeListeners () {
      this.$VueEvent.stop('new_communication', this.listeners.newCommunication)
      this.$VueEvent.stop('update_communication', this.listeners.updateCommunication)
      this.$VueEvent.stop('delete_communication', this.listeners.deleteCommunication)
      this.$VueEvent.stop(CalendarV2BookingEvents.CREATED, this.listeners.calendarV2BookingCreated)
      this.$VueEvent.stop(CalendarV2BookingEvents.UPDATED, this.listeners.calendarV2BookingUpdated)
      this.$VueEvent.stop(CalendarV2BookingEvents.CANCELLED, this.listeners.calendarV2BookingCancelled)
      this.$VueEvent.stop('contact_updated', this.listeners.contactUpdated)
      this.$VueEvent.stop('contact_audit_created', this.listeners.contactAuditCreated)
      this.$VueEvent.stop('fetch_contact_info', this.listeners.fetchContactInfo)
      this.$VueEvent.stop(CONTACT_PHONE_NUMBERS_CHANGED_EVENT, this.listeners.contactPhoneNumbersChanged)
      this.$VueEvent.stop('update-contact-in-group', this.listeners.updateContactInGroup)
    },

    resetScrollIntervals () {
      clearInterval(this.scrollInterval)
      clearInterval(this.containerElInterval)
      this.isScrolling = false
    },

    isCommOrAuditExists (communication, data) {
      // if communication and data is a communication (has type attribute),
      // check if ids match
      if ('type' in communication &&
        'type' in data &&
        communication.id === data.id) {
        return true
      }

      // if communication and data is a contact audit (no type attribute),
      // check if ids match. Else, false
      return !('type' in communication) &&
        !('type' in data) &&
        communication.id === data.id
    },

    isNotSameContact (contactId) {
      const isContactIdEmpty = [null, undefined].includes(contactId)
      const isContactEmpty = _.isEmpty(this.contact)
      const isNotSameContact = !isContactEmpty &&
        !isContactIdEmpty &&
        parseInt(this.contact.id) !== parseInt(contactId)

      return isContactEmpty ||
        isContactIdEmpty ||
        isNotSameContact
    },

    addNewCommunication (data) {
      if (this.smsOnly &&
        data.type !== CommunicationTypes.SMS) {
        return false
      }

      // checks if contact is the same in communication
      if (this.isNotSameContact(data.contact_id)) {
        return false
      }

      // check new communication exists in the old list
      const found = this.communicationsAndAudits
        .find(communication => this.isCommOrAuditExists(communication, data))

      if (!found) {
        // push new data to top of array
        this.communicationsAndAudits.push(data)
        this.removeDuplicateCommunicationsAndAudits()
        this.sortCommunicationsAndAudits()
        this.scrollMessages()
      }
    },

    processContactUpdate (contact, communication = null, deleteCommsAndAudits = false) {
      const contactEvent = this.$jsonClone(contact)
      let newCommunication = null
      if (communication) {
        newCommunication = this.$jsonClone(communication)
      }

      // check if communication's contact is the same as the current contact
      if (parseInt(contact.id) === parseInt(this.contact.id)) {
        const updatedContact = this.$jsonClone(this.contact)
        // add the v2 contact attributes that we need
        Object.assign(contact, this.addV2ContactAttributes(contactEvent, newCommunication, updatedContact))

        if (deleteCommsAndAudits) {
          delete contact.communications_and_audits
        }

        Object.assign(updatedContact, contact)
        this.setContact(updatedContact)
        this.setContactClone(updatedContact)
        this.updateSelectedContact(updatedContact)

        // update the contact/task in Inbox
        this.$VueEvent.fire('contact_updated_from_contact_mixin', contact)
      }

      // update contact in inbox's group of contacts/tasks
      if (!_.isEmpty(newCommunication)) {
        this.$VueEvent.fire('inbox_contact_updated', {
          contact: contactEvent,
          communication: newCommunication
        })
      }
    },

    removeDuplicateCommunicationsAndAudits: _.debounce(function () {
      this.communicationsAndAudits = _.uniqBy(this.communicationsAndAudits, 'id')
    }, 500),

    updateCommunication (data) {
      // checks if contact is the same in communication
      if (this.isNotSameContact(data.contact_id)) {
        return false
      }

      // check data loaded
      if (!_.isEmpty(this.communicationsAndAudits)) {
        // check new communication exists in the old list
        const index = this.communicationsAndAudits
          .findIndex(communication => this.isCommOrAuditExists(communication, data))

        if (index > -1) {
          // update communication
          Object.assign(this.communicationsAndAudits[index], data)
        }
      }
    },

    deleteCommunication (data) {
      // checks if contact is the same in communication
      if (this.isNotSameContact(data.contact_id)) {
        return false
      }

      // check data loaded
      if (!_.isEmpty(this.communicationsAndAudits)) {
        // try to find the communication
        const index = this.communicationsAndAudits
          .findIndex(communication => this.isCommOrAuditExists(communication, data))

        if (index > -1) {
          // remove it from the list
          this.communicationsAndAudits.splice(index, 1)
        }
      }
    },

    calendarV2BookingFeedItemId (bookingId) {
      return `calendar-v2-booking-${bookingId}`
    },

    calendarV2BookingActivityLogFeedItemId (bookingId, activityLogIndex) {
      return `calendar-v2-activity-log-${bookingId}-${activityLogIndex}`
    },

    isCalendarV2FeedItem (item) {
      return [CALENDAR_V2_BOOKING_SOURCE, CALENDAR_V2_ACTIVITY_LOG_SOURCE].includes(item?.source)
    },

    /**
     * Where a booking sits in the feed: the moment it was booked or last changed, never the date
     * it was booked for. That date is usually weeks out, which would put the card below every
     * message in the conversation. The card body reads start_at off the booking, so the
     * appointment time itself still shows.
     */
    calendarV2BookingFeedTimestamp (booking) {
      return booking.updated_at || booking.created_at
    },

    mapCalendarV2BookingToFeedItem (booking) {
      return {
        id: this.calendarV2BookingFeedItemId(booking.id),
        source: CALENDAR_V2_BOOKING_SOURCE,
        created_at: this.calendarV2BookingFeedTimestamp(booking),
        booking
      }
    },

    mapCalendarV2BookingActivityLogsToFeedItems (booking) {
      const activityLogs = booking.activity_logs || []

      return activityLogs.map((activityLog, activityLogIndex) => ({
        id: this.calendarV2BookingActivityLogFeedItemId(booking.id, activityLogIndex),
        source: CALENDAR_V2_ACTIVITY_LOG_SOURCE,
        created_at: activityLog.created_at,
        activityLog,
        booking
      }))
    },

    async fetchContactCalendarV2Bookings (contactId, isStale = () => false) {
      return talk2Api.V2.calendarConnections.listContactBookings(contactId).then(response => {
        if (isStale()) {
          return
        }

        const bookings = response.data.data || []
        const bookingFeedItems = bookings.map(booking => this.mapCalendarV2BookingToFeedItem(booking))
        const activityLogFeedItems = bookings.flatMap(booking => this.mapCalendarV2BookingActivityLogsToFeedItems(booking))

        this.communicationsAndAudits = this.communicationsAndAudits
          .concat(bookingFeedItems, activityLogFeedItems)

        this.sortCommunicationsAndAudits()
      })
    },

    addNewCalendarV2Booking (booking) {
      // checks if contact is the same as the booking
      if (this.isNotSameContact(booking.contact_id)) {
        return false
      }

      const feedItemId = this.calendarV2BookingFeedItemId(booking.id)
      const found = this.communicationsAndAudits.find(item => item.id === feedItemId)

      if (!found) {
        this.communicationsAndAudits.push(this.mapCalendarV2BookingToFeedItem(booking))
        this.sortCommunicationsAndAudits()
      }
    },

    appendNewestCalendarV2BookingActivityLog (booking) {
      const activityLogs = booking.activity_logs || []

      if (!activityLogs.length) {
        return
      }

      const newestActivityLogIndex = activityLogs.length - 1
      const feedItemId = this.calendarV2BookingActivityLogFeedItemId(booking.id, newestActivityLogIndex)
      const alreadyAdded = this.communicationsAndAudits.some(item => item.id === feedItemId)

      if (alreadyAdded) {
        return
      }

      this.communicationsAndAudits.push({
        id: feedItemId,
        source: CALENDAR_V2_ACTIVITY_LOG_SOURCE,
        created_at: activityLogs[newestActivityLogIndex].created_at,
        activityLog: activityLogs[newestActivityLogIndex],
        booking
      })

      this.sortCommunicationsAndAudits()
    },

    updateCalendarV2Booking (booking) {
      // checks if contact is the same as the booking
      if (this.isNotSameContact(booking.contact_id)) {
        return false
      }

      const feedItemId = this.calendarV2BookingFeedItemId(booking.id)
      const index = this.communicationsAndAudits.findIndex(item => item.id === feedItemId)

      if (index === -1) {
        return
      }

      this.communicationsAndAudits[index].booking = booking
      this.communicationsAndAudits[index].created_at = this.calendarV2BookingFeedTimestamp(booking)
      this.appendNewestCalendarV2BookingActivityLog(booking)
      this.sortCommunicationsAndAudits()
    },

    // A widget has no contacts list behind it, so a contact that is gone gets its own screen.
    redirectedWidgetToContactDeleted (response) {
      if (!this.$route.meta?.isWidget || response?.status !== 404) {
        return false
      }

      // The texting widget is embedded by more than one CRM, so the error page follows the host.
      const platform = this.$store.state.widgetPlatform
      const errorPath = platform === WIDGET_PLATFORMS.SALESFORCE
        ? '/errors/salesforce'
        : '/errors/hubspot'

      this.$router.replace({
        path: errorPath,
        query: {
          error_title: CONTACT_DELETED_WIDGET_ERROR_TITLE,
          error_message: CONTACT_DELETED_WIDGET_ERROR_MESSAGE
        }
      }).catch(() => {})

      return true
    },

    fetchFailedNotification (response) {
      let message = 'Cannot find Contact.'
      const messageWithVisibilityLimit = ' Check your Contacts Visibility settings.'
      const hasVisibilityLimit = this.profile.contacts_visibility !== CONTACTS_ACCESS_EVERYONE
      message += hasVisibilityLimit ? messageWithVisibilityLimit : ''

      if (response?.status === 404 && !this.fetchFailed) {
        this.$generalNotification(message, 'error-redirect', 5000, hasVisibilityLimit, {
          path: `/settings/visibility`
        })
      } else if (!this.fetchFailed) {
        this.$handleErrors(response)
      }

      this.fetchFailed = true
    },

    async fetchContactInfo (isFetchContact = true, contactId = null, skipRouterPush = false) {
      const contactIdToFetch = contactId || this.contactId

      // Sanity check: if contact id is actually one of the lists, take person to the list
      if (Object.values(DEFAULT_PINNED_LIST).map(item => item.id).includes(contactIdToFetch)) {
        if (skipRouterPush) {
          return
        }

        const path = '/contacts/list/' + contactIdToFetch

        this.$router.push({ path })

        return
      }

      // Sanity check: if contact id is actually a weird number, just redirect to inbox
      if (isNaN(Number.parseInt(contactIdToFetch))) {
        if (skipRouterPush) {
          return
        }

        this.$router.push({ name: TEAMINBOXES_MENU_TITLE })
        return
      }

      this.communicationsAndAudits = []
      this.communicationsPage = 1
      this.hasMoreCommunications = true
      this.loadingContact = true
      this.loadingContactCommunications = true
      this.fetchFailed = false

      if (!contactIdToFetch) {
        console.log('Failed to fetch contact info: Missing contact id!')
        this.loadingContact = false
        this.loadingContactCommunications = false
        return false
      }

      this.source.cancel('Fetch contact info operation canceled by the user.')
      this.source = this.cancelToken.source()
      const requestId = ++this.currentContactFetchRequestId
      const isStaleRequestCycle = () => this.currentContactFetchRequestId !== requestId

      const handleAuxRequestError = (err) => {
        if (isStaleRequestCycle()) {
          return
        }

        if ((this.$axios && this.$axios.isCancel && this.$axios.isCancel(err)) || window.axios?.isCancel?.(err)) {
          return
        }

        // Don't show "Cannot find Contact" from auxiliary endpoints.
        // Only the primary contact request should decide contact-not-found UX.
        if (err?.response?.status === 404) {
          return
        }

        this.fetchFailedNotification(err?.response)
      }

      // get contact phone numbers; shares its counter with refreshContactPhoneNumbers, so the newer list wins
      const phoneNumbersRequestId = ++this.phoneNumbersRequestId
      const phoneNumbersCall = this.teamInbox
        ? talk2TeamInboxApi.contact.getPhoneNumbers(contactIdToFetch)
        : talk2Api.V1.contact.getPhoneNumbers(contactIdToFetch)

      phoneNumbersCall
        .then(response => {
          if (isStaleRequestCycle() || phoneNumbersRequestId !== this.phoneNumbersRequestId) {
            return
          }

          this.setContactPhoneNumbers(response.data)
        }).catch(err => {
          handleAuxRequestError(err)
        })

      // get contact's communication summary
      talk2Api.V1.contact.getCommunicationsSummary(contactIdToFetch)
        .then(response => {
          if (isStaleRequestCycle()) {
            return
          }

          // Object.keys(response.data.summaries).forEach(key => response.data.summaries[key] = response.data.summaries[key] || 0)
          this.setCommunicationSummary(response.data)
        }).catch(err => {
          handleAuxRequestError(err)
        })

      this.setSequenceInfoLoading(true)

      // get contact's sequence info
      talk2Api.V1.contact.getSequenceInfo(contactIdToFetch)
        .then(response => {
          if (isStaleRequestCycle()) {
            return
          }

          this.setSequenceInfo(response.data)
          this.setSequenceInfoLoading(false)
        }).catch((err) => {
          if (!isStaleRequestCycle()) {
            this.setSequenceInfoLoading(false)
          }

          handleAuxRequestError(err)
        })

      // get contact's contact attributes
      talk2Api.V1.contact.getAttributes(contactIdToFetch)
        .then(response => {
          if (isStaleRequestCycle()) {
            return
          }

          this.setContactAttributes(_.cloneDeep(response.data))
        }).catch(err => {
          handleAuxRequestError(err)
        })

      // get contact's CalendarV2 bookings
      this.fetchContactCalendarV2Bookings(contactIdToFetch, isStaleRequestCycle)
        .catch(err => {
          handleAuxRequestError(err)
        })

      // get contact's communications
      this.fetchContactCommunications(contactIdToFetch, false, false)
        .then(res => {
          this.loadingContact = false

          if (!res) {
            this.loadingContactCommunications = false

            return
          }

          // if route has communication id
          // until id is found
          if (res && this.hasCommunication()) {
            this.loadingContactCommunications = true
            this.fetchContactCommunicationsUntilFound()
          }

          this.scrollMessages()
        }).catch(err => {
          handleAuxRequestError(err)

          if (!isStaleRequestCycle()) {
            this.loadingContactCommunications = false
          }
        })

      if (!isFetchContact) {
        this.$VueEvent.fire('contact_activity_clear_change_from_fetch')

        return
      }

      // get contact's info
      const apiCall = this.teamInbox
        ? talk2TeamInboxApi.contact.show(contactIdToFetch, {
          cancelToken: this.source.token
        })
        : this.$axios.get(`/api/v2/contacts/${contactIdToFetch}`, {
          cancelToken: this.source.token
        })

      return apiCall.then(res => {
        if (isStaleRequestCycle()) {
          return
        }

        this.$VueEvent.fire('contact_activity_clear_change_from_fetch')

        if (res) {
          this.$VueEvent.fire('contact_activity_update_contact_from_fetch', res.data)
          this.maybeShowRedirectedContactToast(contactIdToFetch, res.data)

          return res
        }
      }).catch(err => {
        if (isStaleRequestCycle()) {
          return
        }

        this.$VueEvent.fire('contact_activity_clear_change_from_fetch')

        if (this.$axios.isCancel(err) && err) {
          console.log('Request canceled', err.message)
          this.loadingContact = false

          return
        }

        const redirectContactId = err?.response?.data?.redirect_contact_id
        if (err?.response?.status === 301 && redirectContactId) {
          const redirectPath = this.$route.path.replace(/\/contacts\/\d+/, `/contacts/${redirectContactId}`)

          this.setRedirectedContactToastTarget(redirectContactId)
          this.$router.replace({ path: redirectPath })
          return
        }

        if (this.redirectedWidgetToContactDeleted(err?.response)) {
          return
        }

        this.fetchFailedNotification(err.response)

        // inside Team Inboxes - stay on the current page
        if (this.$route.name.includes(TEAMINBOXES_MENU_TITLE)) {
          return
        }

        // not in Team Inboxes - redirect to contacts page
        this.$router.push({ path: '/contacts' })
      }).finally(() => {
        if (isStaleRequestCycle()) {
          return
        }

        this.loadingContact = false
        this.loadingContactCommunications = false
      })
    },

    showContactInfo (contactId, forceClearLoading = false) {
      this.cleanUpContactsLastUsedLinesWatcher()
      this.mapCommunicationsData()

      if (this.teamInbox) {
        let inboxId = this.teamInboxId
        if (this.isAllInboxesRoute) {
          inboxId = +this.$route.query.inboxId
        }

        const key = `${inboxId}-${contactId}`
        if (this.contactsLastUsedLines.has(key)) {
          this.selectedCampaignId = this.contactsLastUsedLines.get(key)
        } else {
          this.waitForContactsLastUsedLines(key)
        }
      } else {
        // sanity check and clear
        if (this.selectedCampaignId) {
          this.selectedCampaignId = null
        }

        // 1. if contact has initial campaign and there were no communications select initial campaign
        if (!this.communicationsAndAudits.length && this.contact && this.contact.initial_campaign_id) {
          console.log('selectedCampaignId - condition 1', {
            contact_id_param: contactId,
            contact_id: this.contact.id,
            contact_initial_campaign_id: this.contact.initial_campaign_id
          })
          this.selectedCampaignId = this.contact.initial_campaign_id
        }

        // 2. if contact has communications select last communication campaign
        if (!this.selectedCampaignId && this.communicationsAndAudits.length) {
          const communicationTypes = [CommunicationTypes.SMS, CommunicationTypes.CALL]

          // Get the latest communication that is either SMS or CALL
          const latestCommunication = _.find(_.orderBy(this.communicationsAndAudits, item => item.created_at, ['desc']), item => {
            return communicationTypes.includes(item.type)
          })

          if (latestCommunication) {
            console.log('selectedCampaignId - condition 2', {
              contact_id: contactId,
              communication_id: latestCommunication.id,
              communication_campaign_id: latestCommunication.campaign_id
            })
            this.selectedCampaignId = latestCommunication.campaign_id
          }
        }

        // 3. if user has a personal line and contact does not have an initial line
        const userCampaignId = _.get(this.profile, 'campaign_id', null)

        if (!this.selectedCampaignId && userCampaignId) {
          console.log('selectedCampaignId - condition 3', {
            contact_id: contactId,
            user_campaign_id: userCampaignId
          })
          this.selectedCampaignId = userCampaignId
        }

        // 4. if contact doesn't have situation 1 and 2 and selected_contact_campaigns has one campaign select the campaign
        const selectedContactFirstCampaignId = _.get(this.selectedContactCampaigns, '[0].id', null)

        if (!this.selectedCampaignId && selectedContactFirstCampaignId) {
          console.log('selectedCampaignId - condition 4', {
            contact_id: contactId,
            selected_contact_first_campaign_id: selectedContactFirstCampaignId
          })
          this.selectedCampaignId = selectedContactFirstCampaignId
        }

        // 5. if contact doesn't have situation 1 and 2 and 3 and company has one campaign select that campaign
        const firstCampaignId = _.get(this.campaigns, '[0].id', null)

        if (!this.selectedCampaignId && !selectedContactFirstCampaignId && firstCampaignId) {
          console.log('selectedCampaignId - condition 5', {
            contact_id: contactId,
            first_campaign_id: firstCampaignId
          })
          this.selectedCampaignId = firstCampaignId
        }

        // 6. if contact doesn't have situation 1 and 2 and 3 and 4 and company has more then one campaign select the first one
        if (!this.selectedCampaignId && firstCampaignId) {
          console.log('selectedCampaignId - condition 6', {
            contact_id: contactId,
            first_campaign_id: firstCampaignId
          })
          this.selectedCampaignId = firstCampaignId
        }
      }

      this.selectedPhoneNumber = this.contact ? this.contact.phoneNumber : this.selectedPhoneNumber

      this.scrollMessages()

      if (!this.smsOnly && (storage.local.getItem('PREVIOUS_ROUTE_NAME') !== 'Contacts' || forceClearLoading)) {
        this.loadingContactCommunications = false
      }
    },

    waitForContactsLastUsedLines (key) {
      this.stopContactsLastUsedLinesWatcher = this.$watch('contactsLastUsedLinesUpdatedAt', () => {
        if (this.contactsLastUsedLines.has(key) && !this.selectedCampaignId) {
          this.selectedCampaignId = this.contactsLastUsedLines.get(key)
          this.cleanUpContactsLastUsedLinesWatcher()
        }
      })

      setTimeout(() => {
        this.cleanUpContactsLastUsedLinesWatcher()
      }, 5000)
    },

    cleanUpContactsLastUsedLinesWatcher () {
      if (this.stopContactsLastUsedLinesWatcher) {
        this.stopContactsLastUsedLinesWatcher()
      }

      this.stopContactsLastUsedLinesWatcher = null
    },

    updateSelectedContact (contact) {
      if (_.isEmpty(contact) || _.isEmpty(this.contact) || (contact.id !== this.contact.id)) {
        return
      }

      contact.communications_and_audits = _.get(this.contact, 'communications_and_audits', [])
    },

    updateSelectedContactAudit (audit) {
      if (_.isEmpty(audit) || _.isEmpty(this.contact)) {
        return
      }

      const found = this.communicationsAndAudits
        .find(communication => this.isCommOrAuditExists(communication, audit))

      if (!found) {
        this.communicationsAndAudits.push(audit)
      }
    },

    /**
     * api-core pages the audits off last_audit_created_at, so the cursor has to be the oldest
     * loaded communication or audit. CalendarV2 bookings share this array and can sort ahead of
     * both, and their timestamp would move the audit window onto rows already on screen.
     */
    lastAuditCreatedAtCursor () {
      const candidates = this.communicationsAndAudits
        .filter(item => !this.isCalendarV2FeedItem(item) && item?.created_at)
      const oldest = _.minBy(candidates, item => window.moment.utc(item.created_at).valueOf())

      return oldest?.created_at || null
    },

    async fetchContactCommunications (contactId, skipContactInfo = true, useDefaultCatch = true) {
      this.communicationApiSource.cancel('fetchContactCommunications operation canceled by the user.')
      this.communicationApiSource = this.communicationApiCancelToken.source()
      const lastAuditCreatedAt = this.lastAuditCreatedAtCursor()

      const params = {}

      // Add inbox_id when in Team Inbox context
      if (this.teamInbox && this.teamInboxId) {
        params.inbox_ids = [this.teamInboxId]
      }

      if (this.isAllInboxesRoute) {
        delete params.inbox_ids

        // send inbox_ids filter if applied
        if (this.$store.state.TeamInbox.activeFilters.inboxes?.length) {
          params.inbox_ids = this.$store.state.TeamInbox.activeFilters.inboxes.map(inboxId => parseInt(inboxId))
        }
      }

      // Use Team Inbox API when in Team Inbox context
      const requestParams = {
        page: this.communicationsPage,
        per_page: this.communicationsPerPage,
        last_audit_created_at: lastAuditCreatedAt,
        ...params
      }

      const apiCall = this.teamInbox
        ? talk2TeamInboxApi.contact.getCommunications(contactId, requestParams, this.communicationApiSource.token)
        : this.$axios.get(`/api/v1/contact/${contactId}/communications`, {
          params: requestParams,
          cancelToken: this.communicationApiSource.token
        })

      return apiCall.then(res => {
        if (res.data.data && res.data.data.length) {
          this.communicationsAndAudits = this.communicationsAndAudits.filter(item => 'id' in item)
          this.communicationsAndAudits = res.data.data.concat(this.communicationsAndAudits)
          // Prepending only puts the new page ahead of the old one. CalendarV2 bookings are
          // fetched in parallel and land in the same array, so the feed needs a full re-sort.
          this.sortCommunicationsAndAudits()
        }

        this.hasMoreCommunications = res.data.has_more_pages
        this.communicationsPage++

        if (!skipContactInfo) {
          this.showContactInfo(this.contactId, true)
        }

        return res
      }).catch(err => {
        if (err.response && err.response.status === 403) {
          // No access to contact
          this.$router.replace({ name: TEAMINBOXES_MENU_TITLE })
          this.$generalNotification(err.response.data?.error || 'You do not have access to this contact', 'error')
        }

        if (!useDefaultCatch) {
          return
        }

        if (window.axios.isCancel(err)) {
          console.log('Request canceled', err.message)
        }

        this.$handleErrors(err.response)

        this.loadingContactCommunications = false
        console.log(err)
      })
    },

    fetchContactCommunicationsUntilFound (tryCount = 1) {
      if (tryCount === 1) {
        this.resetScrollIntervals()
      }

      if (tryCount > 10) {
        this.loadingContactCommunications = false
        this.$generalNotification('Communication is too old for automatic scrolling', 'error')
        this.resetScrollIntervals()
        return
      }

      this.loadingContactCommunications = true

      // we found the activity, scroll to it
      if (this.isCommunicationFound()) {
        this.scrollIntoActivity()
        this.loadingContactCommunications = false

        return
      }

      // fetch communications until we found the activity id
      this.fetchContactCommunications(this.contactId).then(res => {
        if (!res) {
          this.scrollMessages()
          this.loadingContactCommunications = false

          return
        }

        if (res.data.has_more_pages) {
          tryCount++
          this.fetchContactCommunicationsUntilFound(tryCount)
        } else if (this.isCommunicationFound()) {
          this.scrollIntoActivity()
          this.loadingContactCommunications = false
        } else {
          this.scrollMessages()
          this.loadingContactCommunications = false
        }
      }).catch(() => {
        this.loadingContactCommunications = false
        this.resetScrollIntervals()
      })
    },

    mapCommunicationsData () {
      this.selectedContactCampaigns = []

      if (!this.contact) {
        return
      }

      this.communicationsAndAudits.map((o) => {
        if (o.type !== undefined) {
          const found = this.selectedContactCampaigns.find(cmp => cmp === o.campaign_id)

          if (!found) {
            this.selectedContactCampaigns.push(o.campaign_id)
          }

          o.tag_ids = !_.isEmpty(o.tags) ? o.tags.map((a) => a.id) : []
        }
      })
    },

    loadMorePreviousActivities () {
      this.isLoadingPreviousActivities = true

      this.fetchContactCommunications(this.contactId).then(() => {
        this.isLoadingPreviousActivities = false
      }).catch(() => {
        this.isLoadingPreviousActivities = false
      })
    },

    resetSelectedContact () {
      if (!this.cancelToken) {
        this.cancelToken = this.$axios.CancelToken
      }

      this.source = this.cancelToken.source()
      this.contactId = null
      this.selectedCampaignId = null
      this.selectedPhoneNumber = null
    },

    markAllAsRead () {
      if (this.contact) {
        this.loadingMarkAsRead = true

        const params = {}

        if (this.isAllInboxesRoute) {
          params.all_inboxes = true
        } else if (this.teamInbox && this.teamInboxId) {
          params.ring_group_id = this.teamInboxId
        }

        const apiCall = this.teamInbox
          ? talk2TeamInboxApi.contact.markAsRead(this.contact.id, params)
          : this.$axios.post(`/api/v1/contact/${this.contact.id}/mark-as-read`, params)

        apiCall.then(res => {
          this.loadingMarkAsRead = false

          for (let index in this.communicationsAndAudits) {
            if (typeof this.communicationsAndAudits[index].is_read !== 'undefined' &&
              (!this.teamInboxId || this.communicationsAndAudits[index].ring_group_id === this.teamInboxId)
            ) {
              this.communicationsAndAudits[index].is_read = true
            }
          }

          if (!this.teamInbox || !this.$VueEvent.hasListeners('mark_contact_communications_all_as_read_processed')) {
            // If no team inbox or no listeners for the processed event, we need to fire the events to release the button right away
            this.$VueEvent.fire('mark_contact_communications_all_as_read', this.contact)
            this.$VueEvent.fire('contact_updated', this.contact)
          } else {
            // the Team Inbox shows it right away; the processed event reconciles it
            this.$VueEvent.fire('teaminbox_contact_marked_as_read', this.contact)
          }
        }).catch(err => {
          this.$handleErrors(err.response)
          this.loadingMarkAsRead = false
        })
      }
    },

    scrollMessages () {
      if (this.isScrolling) return

      const counter = { data: 0 }
      clearInterval(this.contactActivitiesInterval)

      this.contactActivitiesInterval = setInterval(() => {
        if (this.$refs.contactActivities) {
          this.$refs.contactActivities.scrollMessages()
          clearInterval(this.contactActivitiesInterval)
        }

        counter.data++

        if (counter.data > 180) {
          clearInterval(this.contactActivitiesInterval)
        }
      }, 250)
    },

    hasCommunication () {
      return this.$route.params.communicationId
    },

    isCommunicationFound () {
      const communicationId = this.$route.params.communicationId
      return communicationId &&
        !!this.communicationsAndAudits.find(communication => 'type' in communication &&
          communication.id.toString() === communicationId.toString())
    },

    isHashActivityType () {
      if (!this.$route.hash) {
        return false
      }

      let hasActivity = false

      for (let type of this.activityTypes) {
        if (this.$route.hash.includes(type)) {
          hasActivity = true
          break
        }
      }

      if (!hasActivity) {
        return false
      }

      let hash = this.$route.hash
      hash = hash.split('-')

      // hash only has 2 items: activity type and id
      return hash.length === 2
    },

    isHashActivityFound () {
      const hash = (this.$route.hash.replace('#', '')).split('-')
      const id = hash[1].trim()

      return !!(hash[0] === 'communication'
        ? this.communicationsAndAudits.find(communication => 'type' in communication &&
          communication.id.toString() === id)
        : this.communicationsAndAudits.find(communication => !('type' in communication) &&
          communication.id.toString() === id))
    },

    scrollIntoActivity () {
      if (this.isScrolling) return
      this.isScrolling = true

      const communicationId = this.$route.params.communicationId
      const communication = this.communicationsAndAudits.find(communication => communication.id.toString() === communicationId.toString())

      if (!communication) {
        this.isScrolling = false
        return
      }

      const ref = (communication.type !== undefined ? 'communication-' : 'contact-audit-') + communication.id
      let count = 0
      let communicationActivity = null

      clearInterval(this.scrollInterval)

      this.scrollInterval = setInterval(() => {
        communicationActivity = (this.$refs.contactActivities)
          ? _.get(this.$refs.contactActivities.$refs, `${ref}.0`, null)
          : null

        if (communicationActivity) {
          communicationActivity.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start'
          })
          // highlight the activity
          this.highlightActivity(communicationActivity)
          clearInterval(this.scrollInterval)
          this.isScrolling = false
        }

        // if we've been waiting for too long to load,
        // clear this interval
        if (count >= 120) {
          clearInterval(this.scrollInterval)
          this.isScrolling = false
        }

        count++
      }, 250)
    },

    highlightActivity (commActivity) {
      const element = commActivity.$el

      if (!element) {
        return
      }

      // Clear any existing highlight
      const highlighted = document.querySelector('.shine')
      if (highlighted) {
        highlighted.classList.remove('shine')
      }

      // Add new highlight
      element.classList.add('shine')

      // Remove highlight after 5 seconds
      setTimeout(() => {
        element.classList.remove('shine')
      }, 5000)

      if (!_.isEmpty(commActivity.$refs) && commActivity.$refs.communicationInfo.$refs.communicationInfoExpansionItem) {
        commActivity.$refs.communicationInfo.$refs.communicationInfoExpansionItem.show()
        let counter = 0
        let containerEl = null

        // Clear any existing container interval
        clearInterval(this.containerElInterval)

        this.containerElInterval = setInterval(() => {
          containerEl = document.querySelector('.contact-activities .scrollbar-white')

          if (containerEl) {
            if (!this.isScrolling) {
              containerEl.scrollTop = element.offsetTop
            }
            clearInterval(this.containerElInterval)
          }

          counter++

          if (counter > 120) {
            clearInterval(this.containerElInterval)
          }
        }, 500)
      }
    },

    checkEmailCapability () {
      const mailIntegrationEnabled = this.currentCompany.sendgrid_integration_enabled || this.currentCompany.mailgun_integration_enabled

      if (this.currentCompany && mailIntegrationEnabled) {
        this.canEmail = true

        return
      }

      this.canEmail = this.selectedCampaign.email_intake && this.selectedCampaign.email_intake_route_id
    },

    updateMessageComposer () {
      if (this.selectedCampaign && this.selectedCampaign.id && this.contact && this.contact.id) {
        this.checkEmailCapability()
      }
    },

    updateLineIncomingNumber () {
      if (this.contact && this.contact.id && !_.isEmpty(this.selectedCampaign)) {
        this.setLineIncomingNumberLoading(true)

        let params = null
        if (this.isCallContext) {
          params = { is_call_context: this.isCallContext }
        }

        const apiCall = this.teamInbox
          ? talk2TeamInboxApi.contact.getIncomingNumber(this.contact.id, this.selectedCampaign.id)
          : talk2Api.V1.contact.getLineIncomingNumber(this.contact.id, this.selectedCampaign.id, params)

        apiCall.then(response => {
          this.setLineIncomingNumber(response.data)
        }).finally(() => {
          this.setLineIncomingNumberLoading(false)
        })
      }
    },

    fetchContact: _.debounce(function (shouldShowLoading = true) {
      if (shouldShowLoading) {
        this.selectedContactChanging(true)
      }

      this.processFetchContactInfo((selectedContact) => {
        this.setContact(selectedContact)
        this.setContactClone(selectedContact)
        this.resetChangedContactProperties([])
        this.selectedContactChanging(false)
      })
    }, 1000),

    /**
     * Reload the phone list of the open contact and its primary number, leaving the rest of the page as it is.
     *
     * @param {number|string} contactId
     */
    refreshContactPhoneNumbers (contactId) {
      // During a switch the store still holds the previous contact while the next one's fetch is out.
      const isFetchingAnother = () => !_.isNil(this.contactId) && parseInt(contactId) !== parseInt(this.contactId)

      if (_.isEmpty(this.contact) || parseInt(contactId) !== parseInt(this.contact.id) || isFetchingAnother()) {
        return Promise.resolve()
      }

      const requestId = ++this.phoneNumbersRequestId
      const phoneNumbersCall = this.teamInbox
        ? talk2TeamInboxApi.contact.getPhoneNumbers(this.contact.id)
        : talk2Api.V1.contact.getPhoneNumbers(this.contact.id)

      return phoneNumbersCall
        .then(response => {
          // The user may have opened another contact, or a newer refresh started, while the request was out.
          if (requestId !== this.phoneNumbersRequestId || _.isEmpty(this.contact) || parseInt(contactId) !== parseInt(this.contact.id) || isFetchingAnother()) {
            return
          }

          this.setContactPhoneNumbers(response.data)

          // The composer's To selector reads the list the contact object carries, and the primary may have moved
          // with it; without a contact broadcast nothing else updates the contact's primary.
          const changes = {}
          const listPrimary = response.data.find(phone => phone.is_primary)?.phone_number

          if (Array.isArray(this.contact.phone_numbers)) {
            changes.phone_numbers = response.data
          }

          if (listPrimary && listPrimary !== this.contact.phone_number) {
            changes.phone_number = listPrimary
          }

          if (!_.isEmpty(changes)) {
            this.setContact({ ...this.contact, ...changes })

            const contactClone = this.$store.state.contacts.contactClone

            if (!_.isEmpty(contactClone) && parseInt(contactClone.id) === parseInt(this.contact.id)) {
              this.setContactClone({ ...contactClone, ...changes })
            }
          }
        })
        .catch(err => {
          console.log('err: ', err)
        })
    },

    processFetchContactInfo (callback, isFetchContact = true) {
      this.loadingContactInProgress()

      const fetchCycle = this.fetchContactInfo(isFetchContact)
      // fetchContactInfo bumps the id synchronously, so this is the id of the cycle just started.
      const requestId = this.currentContactFetchRequestId

      return fetchCycle.then(res => {
        // A newer switch owns the spinner and the selected contact.
        if (this.currentContactFetchRequestId !== requestId) {
          return
        }

        if (!res) {
          this.selectedContactChanging(false)
          return
        }

        this.processFetchedContactInfo(res.data, callback)
      }).catch((err) => {
        console.log('err: ', err)
        // this.loadingContactsFailed()
      })
    },

    loadingContactInProgress () {
      if (!this.integrationsDisabled) {
        this.activeNames.push('integrations')
        this.activeNames.push('integration-cards')
      } else {
        this.activeNames = this.activeNames.filter(name => name !== 'integrations' && name !== 'integration-cards')
      }

      if (this.isPushContactToCrmEnabled) {
        this.activeNames.push('push-to-crm')

        return
      }

      this.activeNames = this.activeNames.filter(name => name !== 'push-to-crm')
    },

    processFetchedContactInfo (selectedContact, callback) {
      selectedContact.tag_ids = selectedContact.tags.map((tag) => tag.id)
      this.messageObject.contact = selectedContact
      // TODO: update contact name in title?
      // this.updateBreadcrumbContactName(this.contact)
      this.contact_phone_numbers = []
      this.$VueEvent.fire('contact_selected', this.contactId)

      if (typeof callback === 'function') {
        callback(selectedContact)
      }
    },

    loadingContactsFailed () {
      this.$generalNotification('Failed to load contacts.', 'error')
      this.$router.replace({
        name: 'Contacts'
      })
    },

    loadMoreContacts () {
      if (this.pagination && this.pagination.to && this.filter.page <= this.pagination.to) {
        this.filter.page += 1

        this.getContacts()
          .then(res => {
            this.loadingContact = false
            this.$router.push({
              name: 'Contact',
              params: { contact_id: res.data.data[0].id }
            }).catch(err => {
              console.log(err)
            })
          })
          .catch(err => {
            this.loadingContact = false
            console.log(err)
          })
      } else {
        this.loadingContact = false
      }
    },

    // fixPhone() returns an E.164-shaped string for partial plus-prefixed input, so also require
    // libphonenumber's IS_POSSIBLE verdict. Not isPossibleNumber(): that also accepts
    // IS_POSSIBLE_LOCAL_ONLY lengths, which api-core rejects with a 400 before any lookup.
    // Values libphonenumber cannot parse (short codes, "restricted") still go to the server as before.
    getContactByPhoneNumber (phoneNumber, getContactTry = 1) {
      const fixed = this.$options.filters.fixPhone(phoneNumber)

      if (!fixed) {
        return Promise.reject('Phone number is not valid')
      }

      let possible = true

      try {
        const verdict = window.phoneUtil.isPossibleNumberWithReason(window.phoneUtil.parse(fixed))
        possible = verdict === googlePhone.PhoneNumberUtil.ValidationResult.IS_POSSIBLE
      } catch (e) {
        // not parseable, nothing to judge
      }

      if (!possible) {
        return Promise.reject('Phone number is not valid')
      }

      this.loadingContact = true
      return this.$axios.get('/api/v1/contact/phone-number', {
        params: {
          phone_number: fixed,
          load_last_campaign: true,
          load_info: false
        }
      }).then(res => {
        this.loadingContact = false
        return Promise.resolve(res.data)
      }).catch(err => {
        getContactTry++
        // check if we have found the contact after 3 retries
        if (getContactTry > 3 || [400, 404].includes(err?.response?.status)) {
          // error
          console.log('An error occurred while getting the contact', err?.response?.data || err)
          this.loadingContact = false
          return Promise.reject(err)
        } else {
          this.getContactByPhoneNumber(phoneNumber, getContactTry)
        }
      })
    },

    // Resolve a phone number's timezone before the contact exists, so the
    // dialer's "outside calling hours" alert fires on the first dial to an unsaved number.
    // fixPhone() returns an E.164-shaped string for partial input, so we additionally gate
    // on libphonenumber's isValidNumber() to avoid an API call per keystroke.
    lookupPhoneNumberMeta (phoneNumber) {
      const fixed = this.$options.filters.fixPhone(phoneNumber)

      if (!fixed) {
        return Promise.resolve(null)
      }

      try {
        const parsed = window.phoneUtil.parse(fixed)

        if (!window.phoneUtil.isValidNumber(parsed)) {
          return Promise.resolve(null)
        }
      } catch (e) {
        return Promise.resolve(null)
      }

      return this.$axios.get('/api/v1/phone-number/lookup', {
        params: { phone_number: fixed }
      }).then(res => res.data).catch(err => {
        console.error('Phone number lookup failed', err?.response?.data || err)
        return null
      })
    },

    sortCommunicationsAndAudits: _.debounce(function () {
      this.communicationsAndAudits = _.orderBy(
        this.communicationsAndAudits,
        [
          // Sort on the instant, not the string. Two formats land in this list: communications
          // serialize created_at as 'YYYY-MM-DD HH:mm:ss' UTC, CalendarV2 bookings and activity
          // logs as ISO-8601 with a Z. Compared as strings they agree on the date but not on the
          // time, because ' ' sorts before 'T' at the separator, so a same-day booking ordered
          // against a communication by separator instead of by clock.
          item => window.moment.utc(item.created_at).valueOf(),
          item => {
            if (item.type) {
              return 0
            }

            if (item.source === CALENDAR_V2_BOOKING_SOURCE) {
              return 1
            }

            if (item.source === CALENDAR_V2_ACTIVITY_LOG_SOURCE) {
              return 2
            }

            return 3
          }
        ],
        [
          'asc',
          'asc'
        ]
      )
    }, 500),

    ...mapActions('contacts', [
      'setContact',
      'setContactClone',
      'resetChangedContactProperties',
      'updateContacts',
      'setContactPhoneNumbers',
      'setSequenceInfoLoading',
      'setSequenceInfo',
      'setLineIncomingNumberLoading',
      'setLineIncomingNumber',
      'setCommunicationSummary',
      'setContactAttributes',
      'setIsContactMixinUsed',
      'selectedContactChanging'
    ]),

    ...mapActions('inbox', ['setSelectedContact'])
  },

  watch: {
    'selectedCampaign.id': _.debounce(function (value) {
      this.updateMessageComposer()
      this.updateLineIncomingNumber(this.teamInbox)
    }, 1000),

    contactId: function () {
      this.communicationsPage = 1
    }
  },

  beforeDestroy () {
    this.$VueEvent.stop('fetch_contact_info', this.listeners.fetchContactInfo)
    clearInterval(this.contactActivitiesInterval)
    clearInterval(this.containerElInterval)
    clearInterval(this.scrollInterval)
    this.isScrolling = false
  }
}
